import type {
  QuickBooksBootstrap,
  QuickBooksSyncRecord,
  QuickBooksV2Health,
} from "@crewfsm-extension/quickbooks-contracts";
import { useCallback, useEffect, useMemo, useState } from "react";

import { connectQuickBooksApp, type QuickBooksAppSession } from "./app-session";
import { createQuickBooksApi } from "./quickbooks-api";

export function QuickBooksApp() {
  const [session, setSession] = useState<QuickBooksAppSession | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    let connected: QuickBooksAppSession | null = null;
    void connectQuickBooksApp()
      .then((value) => {
        connected = value;
        if (!active) {
          value.bridge.destroy();
          return;
        }
        applyTheme(value.context.themeContract);
        setSession(value);
      })
      .catch((cause: unknown) => {
        if (active) setError(errorMessage(cause));
      });
    return () => {
      active = false;
      connected?.bridge.destroy();
    };
  }, []);

  if (error)
    return <CenterState title="QuickBooks is unavailable" detail={error} />;
  if (!session)
    return (
      <CenterState
        title="Loading QuickBooks"
        detail="Preparing the secure connection."
      />
    );
  return <QuickBooksWorkspace session={session} />;
}

function QuickBooksWorkspace({ session }: { session: QuickBooksAppSession }) {
  const api = useMemo(
    () =>
      createQuickBooksApi({
        getSessionToken: () => session.bridge.getSessionToken(),
        refreshSessionToken: async () => {
          await session.bridge.refreshContext();
          return session.bridge.getSessionToken();
        },
      }),
    [session.bridge],
  );
  const [data, setData] = useState<QuickBooksBootstrap | null>(null);
  const [health, setHealth] = useState<QuickBooksV2Health | null>(null);
  const [records, setRecords] = useState<QuickBooksSyncRecord[]>([]);
  const [recordStatus, setRecordStatus] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  const load = useCallback(async () => {
    setFailure(null);
    try {
      const [bootstrap, nextHealth, nextRecords] = await Promise.all([
        api.bootstrap(),
        api.v2Health(),
        api.v2Records(recordStatus ? { status: recordStatus } : {}),
      ]);
      setData(bootstrap);
      setHealth(nextHealth);
      setRecords(nextRecords.items);
    } catch (cause) {
      setFailure(errorMessage(cause));
    }
  }, [api, recordStatus]);

  useEffect(() => {
    void session.bridge.setTitle("QuickBooks Online");
    void load();
  }, [load, session.bridge]);

  const connect = async () => {
    setPending("connect");
    try {
      const result = await api.startOAuth();
      const popup = window.open(
        result.authorizationUrl,
        "crewfsm-quickbooks-oauth",
        "popup,width=720,height=780",
      );
      if (!popup) throw new Error("Allow pop-ups to connect QuickBooks.");
      for (let attempt = 0; attempt < 60; attempt += 1) {
        await delay(2_000);
        const next = await api.bootstrap();
        setData(next);
        if (next.connection.status === "CONNECTED") {
          popup.close();
          await load();
          await session.bridge.showToast("QuickBooks connected.", "SUCCESS");
          return;
        }
        if (popup.closed) break;
      }
      throw new Error("QuickBooks connection was not completed.");
    } catch (cause) {
      await session.bridge.showToast(errorMessage(cause), "CRITICAL");
    } finally {
      setPending(null);
    }
  };

  const sync = async () => {
    setPending("sync");
    try {
      const result = await api.queueV2Sync(false);
      await load();
      await session.bridge.showToast(
        `QuickBooks sync ${result.job.status.toLowerCase()}.`,
        "SUCCESS",
      );
    } catch (cause) {
      await session.bridge.showToast(errorMessage(cause), "CRITICAL");
      await load();
    } finally {
      setPending(null);
    }
  };

  const reconcile = async () => {
    setPending("reconcile");
    try {
      const result = await api.queueV2Sync(true);
      await load();
      await session.bridge.showToast(
        `Reconciliation ${result.job.status.toLowerCase()}.`,
        "SUCCESS",
      );
    } catch (cause) {
      await session.bridge.showToast(errorMessage(cause), "CRITICAL");
      await load();
    } finally {
      setPending(null);
    }
  };

  const redrive = async () => {
    if (!health?.latestJob) return;
    setPending("redrive");
    try {
      await api.redriveV2Job(health.latestJob.id);
      await load();
      await session.bridge.showToast("Sync recovery queued.", "SUCCESS");
    } catch (cause) {
      await session.bridge.showToast(errorMessage(cause), "CRITICAL");
    } finally {
      setPending(null);
    }
  };

  const disconnect = async () => {
    setPending("disconnect");
    try {
      await api.disconnect();
      await load();
      await session.bridge.showToast("QuickBooks disconnected.", "SUCCESS");
    } catch (cause) {
      await session.bridge.showToast(errorMessage(cause), "CRITICAL");
    } finally {
      setPending(null);
    }
  };

  if (failure) {
    return (
      <CenterState
        title="QuickBooks could not load"
        detail={failure}
        onRetry={() => void load()}
      />
    );
  }
  if (!data || !health) {
    return (
      <CenterState
        title="Loading QuickBooks"
        detail="Reading the current connection."
      />
    );
  }

  const connected = health.connection.status === "CONNECTED";
  const recoverableJob =
    health.latestJob &&
    ["FAILED", "PARTIAL", "DEAD_LETTER"].includes(health.latestJob.status);
  return (
    <main className="app-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Accounting</p>
          <h1>QuickBooks Online</h1>
          <p>
            Keep QuickBooks accounting records aligned while CrewFSM remains the
            operational source of truth.
          </p>
        </div>
        <StatusBadge status={health.connection.status} />
      </header>

      <section className="connection-card">
        <div className="connection-copy">
          <span className="integration-mark">qb</span>
          <div>
            <h2>
              {connected ? data.connection.companyName : "Connect a company"}
            </h2>
            <p>
              {connected
                ? "The connection is isolated to this CrewFSM business."
                : data.providerConfigured
                  ? "Authorize the QuickBooks company that should receive accounting data."
                  : "QuickBooks developer credentials must be configured by CrewFSM before connecting."}
            </p>
          </div>
        </div>
        <div className="actions">
          {connected ? (
            <>
              <button
                className="button button-primary"
                disabled={pending !== null}
                onClick={() => void sync()}
              >
                {pending === "sync" ? "Syncing…" : "Sync now"}
              </button>
              <button
                className="button button-secondary"
                disabled={pending !== null}
                onClick={() => void reconcile()}
              >
                {pending === "reconcile" ? "Checking…" : "Reconcile"}
              </button>
              <button
                className="button button-secondary"
                disabled={pending !== null}
                onClick={() => void disconnect()}
              >
                {pending === "disconnect" ? "Disconnecting…" : "Disconnect"}
              </button>
            </>
          ) : (
            <button
              className="button button-primary"
              disabled={!data.providerConfigured || pending !== null}
              onClick={() => void connect()}
            >
              {pending === "connect" ? "Connecting…" : "Connect QuickBooks"}
            </button>
          )}
        </div>
      </section>

      <section className="detail-grid" aria-label="QuickBooks sync health">
        <article className="detail-card">
          <span>Latest job</span>
          <strong>
            {health.latestJob
              ? humanize(health.latestJob.status)
              : "Not synced yet"}
          </strong>
          <p>
            {health.latestJob?.lastErrorMessage ??
              (health.latestJob
                ? `${health.latestJob.recordsSucceeded} of ${health.latestJob.recordsTotal} records completed.`
                : "Changes are queued from signed CrewFSM events or a manual run.")}
          </p>
        </article>
        <article className="detail-card">
          <span>In sync</span>
          <strong>{health.counts.inSync}</strong>
          <p>
            {health.counts.pending} pending and {health.counts.failed} failed.
          </p>
        </article>
        <article className="detail-card">
          <span>Needs review</span>
          <strong>{health.counts.conflicts + health.counts.deadLetter}</strong>
          <p>
            {health.counts.conflicts} conflicts and {health.counts.deadLetter}{" "}
            dead-letter records.
          </p>
        </article>
      </section>

      {health.connection.lastError ? (
        <section className="attention-note" role="alert">
          <h2>Connection needs attention</h2>
          <p>{health.connection.lastError}</p>
        </section>
      ) : null}

      <section className="records-card" aria-labelledby="sync-records-title">
        <div className="records-header">
          <div>
            <h2 id="sync-records-title">Record sync status</h2>
            <p>
              Provider IDs and SyncTokens are evidence; CrewFSM values are not
              overwritten.
            </p>
          </div>
          <div className="records-actions">
            <label>
              <span>Status</span>
              <select
                value={recordStatus}
                onChange={(event) => setRecordStatus(event.target.value)}
              >
                <option value="">All statuses</option>
                <option value="PENDING">Pending</option>
                <option value="IN_SYNC">In sync</option>
                <option value="FAILED">Failed</option>
                <option value="CONFLICT">Conflict</option>
                <option value="DEAD_LETTER">Dead letter</option>
              </select>
            </label>
            {recoverableJob ? (
              <button
                className="button button-secondary"
                disabled={pending !== null}
                onClick={() => void redrive()}
              >
                {pending === "redrive" ? "Queuing…" : "Redrive latest job"}
              </button>
            ) : null}
          </div>
        </div>
        {records.length ? (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Type</th>
                  <th scope="col">CrewFSM ID</th>
                  <th scope="col">Status</th>
                  <th scope="col">QuickBooks ID</th>
                  <th scope="col">Last attempt</th>
                </tr>
              </thead>
              <tbody>
                {records.map((record) => (
                  <tr key={`${record.entityType}:${record.crewFsmId}`}>
                    <td>{humanize(record.entityType)}</td>
                    <td className="mono">{shortId(record.crewFsmId)}</td>
                    <td>
                      <span
                        className={`record-status status-${record.status.toLowerCase()}`}
                      >
                        {humanize(record.status)}
                      </span>
                      {record.errorMessage ? (
                        <small>{record.errorMessage}</small>
                      ) : null}
                    </td>
                    <td className="mono">{record.quickBooksId ?? "—"}</td>
                    <td>
                      {record.lastAttemptAt
                        ? formatDate(record.lastAttemptAt)
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="empty-state">No records match this filter yet.</p>
        )}
      </section>

      <section className="scope-note">
        <h2>Sync V2 boundary</h2>
        <p>
          CrewFSM sends customer, service, tax, invoice, payment, reversal, and
          refund snapshots outbound. Reconciliation reports QuickBooks changes
          as conflicts and never silently imports them into operational records.
        </p>
      </section>
    </main>
  );
}

function StatusBadge({
  status,
}: {
  status: QuickBooksV2Health["connection"]["status"];
}) {
  const connected = status === "CONNECTED";
  return (
    <span
      className={`status-badge ${connected ? "connected" : status === "NEEDS_ATTENTION" ? "attention" : ""}`}
    >
      <span aria-hidden="true" />
      {connected
        ? "Connected"
        : status === "NEEDS_ATTENTION"
          ? "Needs attention"
          : "Not connected"}
    </span>
  );
}

function CenterState({
  title,
  detail,
  onRetry,
}: {
  title: string;
  detail: string;
  onRetry?: () => void;
}) {
  return (
    <main className="center-state">
      <h1>{title}</h1>
      <p>{detail}</p>
      {onRetry ? (
        <button className="button button-secondary" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </main>
  );
}

function applyTheme(theme: QuickBooksAppSession["context"]["themeContract"]) {
  const properties: Record<string, string> = {
    "--app-background": theme.colors.background,
    "--app-surface": theme.colors.surface,
    "--app-foreground": theme.colors.foreground,
    "--app-muted": theme.colors.mutedForeground,
    "--app-primary": theme.colors.primary,
    "--app-primary-foreground": theme.colors.primaryForeground,
    "--app-accent": theme.colors.accent,
    "--app-border": theme.colors.border,
    "--app-ring": theme.colors.ring,
    "--app-success": theme.colors.success,
    "--app-radius-sm": theme.shape.radiusSmall,
    "--app-radius-md": theme.shape.radiusMedium,
    "--app-radius-lg": theme.shape.radiusLarge,
    "--app-font": theme.typography.fontFamily,
  };
  for (const [property, value] of Object.entries(properties)) {
    document.documentElement.style.setProperty(property, value);
  }
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function humanize(value: string): string {
  return value
    .toLowerCase()
    .replaceAll("_", " ")
    .replace(/^./, (letter) => letter.toUpperCase());
}

function shortId(value: string): string {
  return value.length > 20 ? `${value.slice(0, 8)}…${value.slice(-4)}` : value;
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "CrewFSM could not complete this request.";
}
